import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiClient, type ApiPaginatedResponse } from "./_client";
import { type AxiosResponse } from "axios";
import { type ColumnSort } from "@tanstack/react-table";
import { downloadBlob } from "@/lib/utils";

export const documentTemplateKeys = {
  all: ["documentTemplates"] as const,
  lists: () => [...documentTemplateKeys.all, "list"] as const,
  list: (filters: {
    pageSize?: number;
    pageIndex?: number;
    filter?: string;
    ordering?: ColumnSort | string;
  }) =>
    [
      ...documentTemplateKeys.lists(),
      filters.pageSize,
      filters.pageIndex,
      filters.filter,
      filters.ordering,
    ] as const,
  details: () => [...documentTemplateKeys.all, "detail"] as const,
  detail: (id: string) => [...documentTemplateKeys.details(), id] as const,
};

export type DocumentTemplate = {
  id: string;
  category: string;
  title: string;
  authoritative_for: string;
  request_when_missing: string;
  created_at: string;
  updated_at: string;
};

export type DocumentTemplateCreateData = {
  category: string;
  title: string;
  authoritative_for?: string;
  request_when_missing?: string;
};

export type DocumentTemplateEditData = {
  id: string;
  category?: string;
  title?: string;
  authoritative_for?: string;
  request_when_missing?: string;
};

type DocumentTemplatePaginatedResponse =
  ApiPaginatedResponse<DocumentTemplate>;

export const useDocumentTemplates = (
  pageSize: number = 25,
  pageIndex: number = 1,
  filter: string = "",
  ordering?: ColumnSort | string,
): UseQueryResult<DocumentTemplatePaginatedResponse> => {
  let url = `api/documents/document-templates/?page_size=${pageSize}&page=${pageIndex}`;
  if (filter) {
    url += `&search=${filter}`;
  }
  if (ordering) {
    if (typeof ordering === "string") {
      url += `&ordering=${ordering}`;
    } else {
      url += `&ordering=${ordering.desc ? "-" : ""}${ordering.id}`;
    }
  }

  return useQuery<DocumentTemplatePaginatedResponse>({
    queryKey: documentTemplateKeys.list({
      pageSize,
      pageIndex,
      filter,
      ordering,
    }),
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<DocumentTemplatePaginatedResponse>
      >(url);
      return response.data;
    },
    staleTime: 30 * 1000,
  });
};

export const createDocumentTemplate = async (
  data: DocumentTemplateCreateData,
) => {
  return await apiClient.post<DocumentTemplate>(
    "api/documents/document-templates/",
    data,
  );
};

export const updateDocumentTemplate = async (
  data: DocumentTemplateEditData,
) => {
  const response = await apiClient.patch<DocumentTemplate>(
    `api/documents/document-templates/${data.id}/`,
    data,
  );
  return response;
};

export const deleteDocumentTemplate = async (id: string) => {
  return await apiClient.delete(`api/documents/document-templates/${id}/`);
};

export const bulkUploadDocumentTemplates = async (file: File) => {
  const formData = new FormData();
  formData.append("bulkfile", file);
  const response = await apiClient.post<{
    created_count: number;
    updated_count: number;
    errors: string[];
  }>("api/documents/document-templates/bulk-upload/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

// --- Document Type Suggestions ---

export const documentTypeSuggestionKeys = {
  all: ["documentTypeSuggestions"] as const,
  list: (status?: string) =>
    [...documentTypeSuggestionKeys.all, "list", status] as const,
  pendingCount: () =>
    [...documentTypeSuggestionKeys.all, "pendingCount"] as const,
};

export type DocumentTypeSuggestion = {
  id: string;
  suggested_title: string;
  suggested_category: string;
  document_id: string;
  document_filename: string;
  status: "pending" | "approved" | "dismissed";
  occurrence_count: number;
  created_at: string;
  updated_at: string;
};

type DocumentTypeSuggestionPaginatedResponse =
  ApiPaginatedResponse<DocumentTypeSuggestion>;

export const useDocumentTypeSuggestions = (
  status: string = "pending",
): UseQueryResult<DocumentTypeSuggestionPaginatedResponse> => {
  return useQuery<DocumentTypeSuggestionPaginatedResponse>({
    queryKey: documentTypeSuggestionKeys.list(status),
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<DocumentTypeSuggestionPaginatedResponse>
      >(`api/documents/document-type-suggestions/?status=${status}&page_size=100`);
      return response.data;
    },
    staleTime: 30 * 1000,
  });
};

export const useDocumentTypeSuggestionPendingCount = (
  enabled: boolean = true,
): UseQueryResult<number> => {
  return useQuery<number>({
    queryKey: documentTypeSuggestionKeys.pendingCount(),
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<DocumentTypeSuggestionPaginatedResponse>
      >("api/documents/document-type-suggestions/?status=pending&page_size=1");
      return response.data.count;
    },
    staleTime: 30 * 1000,
    enabled,
  });
};

export const approveDocumentTypeSuggestion = async (
  id: string,
  title: string,
  category: string,
) => {
  return await apiClient.post(
    `api/documents/document-type-suggestions/${id}/approve/`,
    { title, category },
  );
};

export const dismissDocumentTypeSuggestion = async (id: string) => {
  return await apiClient.post(
    `api/documents/document-type-suggestions/${id}/dismiss/`,
  );
};

export const exportDocumentTemplates = async () => {
  const response = await apiClient.get(
    "api/documents/document-templates/export/",
    { responseType: "blob" },
  );
  const blob = new Blob([response.data as BlobPart], { type: "text/csv" });
  downloadBlob(blob, "document_templates.csv");
};
